// src/pages/Contact.js
// Contact page — shows email and location info alongside a contact form.
// Form submissions are handled by Netlify Forms (data-netlify="true").
// The hidden form in index.html registers the form fields with Netlify at build time.

import React, { useState } from 'react';
import { contactContent } from '../data/scents';
import { Mail, MapPin, Send } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';

const Contact = () => {
    usePageTitle(
        'Get in Touch',
        'Contact the MAISON D\'OUD team. We respond to all enquiries within 24 hours.',
        'contact maison d\'oud, perfume oil support, fragrance help'
    );

    // Form field state
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });

    // Controls the success confirmation view after submission
    const [submitted, setSubmitted] = useState(false);

    // Prevents double submission while the fetch is in flight
    const [submitting, setSubmitting] = useState(false);

    // Update a single field by name
    const handleChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    // Submit to Netlify Forms using URL-encoded format
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            const formDataEncoded = new URLSearchParams();
            formDataEncoded.append('form-name', 'contact');
            Object.entries(formData).forEach(([key, value]) => {
                formDataEncoded.append(key, value);
            });

            await fetch('/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: formDataEncoded.toString(),
            });

            setSubmitted(true);
            setFormData({ name: '', email: '', subject: '', message: '' });
        } catch (err) {
            console.error('Form submission error:', err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-background min-h-screen pt-32 pb-24 px-6 transition-colors duration-300">
            <div className="max-w-7xl mx-auto">

                {/* Page heading */}
                <div className="text-center mb-16">
                    <h1 className="text-5xl md:text-6xl font-light text-foreground mb-6 tracking-wide">
                        {contactContent.heading}
                    </h1>
                    <p className="text-muted-foreground text-lg max-w-3xl mx-auto leading-relaxed">
                        {contactContent.subtitle}
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">

                    {/* Left — contact info and response promise */}
                    <div>
                        <h2 className="text-3xl font-light text-foreground mb-8">Let's Connect</h2>

                        <div className="space-y-6 mb-12">

                            {/* Email */}
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 bg-[#CEAC8F]/10 rounded-full flex items-center justify-center flex-shrink-0">
                                    <Mail className="w-5 h-5 text-[#CEAC8F]" />
                                </div>
                                <div>
                                    <h3 className="text-foreground font-medium mb-1">Email</h3>
                                    <a href={`mailto:${contactContent.email}`} className="text-muted-foreground hover:text-[#CEAC8F] transition-colors">
                                        {contactContent.email}
                                    </a>
                                </div>
                            </div>

                            {/* Location */}
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 bg-[#CEAC8F]/10 rounded-full flex items-center justify-center flex-shrink-0">
                                    <MapPin className="w-5 h-5 text-[#CEAC8F]" />
                                </div>
                                <div>
                                    <h3 className="text-foreground font-medium mb-1">Location</h3>
                                    <p className="text-muted-foreground">{contactContent.address}</p>
                                </div>
                            </div>
                        </div>

                        {/* Response time promise */}
                        <div className="bg-card rounded-2xl p-8 border border-border">
                            <h3 className="text-foreground text-xl font-light mb-2">Fast Customer Service</h3>
                            <p className="text-muted-foreground text-sm leading-relaxed">
                                We aim to respond to all enquiries within 24 hours. Whether you have a question about a product, an order, or anything else, drop us a message and we'll get back to you.
                            </p>
                        </div>
                    </div>

                    {/* Right — contact form or success confirmation */}
                    <div className="bg-card rounded-2xl p-8 border border-border">
                        <h2 className="text-2xl font-light text-foreground mb-6">Send us a message</h2>

                        {submitted ? (
                            /* Success state after form submission */
                            <div className="text-center py-12">
                                <div className="w-16 h-16 bg-[#CEAC8F]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Send className="w-8 h-8 text-[#CEAC8F]" />
                                </div>
                                <h3 className="text-foreground text-xl font-light mb-2">Message Sent!</h3>
                                <p className="text-muted-foreground text-sm">We'll get back to you within 24 hours.</p>
                                <button
                                    onClick={() => setSubmitted(false)}
                                    className="mt-6 text-[#CEAC8F] text-sm hover:underline"
                                >
                                    Send another message
                                </button>
                            </div>
                        ) : (
                            /* Contact form — data-netlify registers it with Netlify Forms */
                            <form
                                name="contact"
                                method="POST"
                                data-netlify="true"
                                onSubmit={handleSubmit}
                                className="space-y-6"
                            >
                                <input type="hidden" name="form-name" value="contact" />

                                <div>
                                    <label htmlFor="name" className="block text-muted-foreground text-sm mb-2">Your Name</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[#CEAC8F]/50 transition-colors"
                                        placeholder="John Doe"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="email" className="block text-muted-foreground text-sm mb-2">Email Address</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[#CEAC8F]/50 transition-colors"
                                        placeholder="john@example.com"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="subject" className="block text-muted-foreground text-sm mb-2">Subject</label>
                                    <input
                                        type="text"
                                        id="subject"
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleChange}
                                        required
                                        className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[#CEAC8F]/50 transition-colors"
                                        placeholder="Product Inquiry"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="message" className="block text-muted-foreground text-sm mb-2">Message</label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                        rows="6"
                                        className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[#CEAC8F]/50 transition-colors resize-none"
                                        placeholder="Tell us about your inquiry..."
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="w-full bg-[#CEAC8F] text-black py-4 rounded-lg font-medium hover:bg-[#d4b896] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                                >
                                    <Send className="w-5 h-5" />
                                    {submitting ? 'Sending...' : 'Send Message'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Contact;